import api from './api';
import type { AuthResponse } from '../../../shared/types';
import { StorageManager } from '../utils/storageManager';

export const TWO_FACTOR_PENDING_KEY = 'twoFactorPendingToken';
export const TWO_FACTOR_REDIRECT_KEY = 'twoFactorRedirectAfter';

export interface TwoFactorSetupResponse {
  qrCodeDataUrl: string;
  manualEntryKey: string;
  twoFactorMandatory: boolean;
}

export interface TwoFactorStatusResponse {
  twoFactorEnabled: boolean;
  twoFactorMandatory: boolean;
  backupCodesRemaining: number;
}

export function isTwoFactorChallenge(response: AuthResponse): boolean {
  return Boolean(response.requiresTwoFactor || response.requiresTwoFactorSetup);
}

export function storePendingTwoFactorToken(token: string) {
  sessionStorage.setItem(TWO_FACTOR_PENDING_KEY, token);
}

export function getPendingTwoFactorToken(): string | null {
  return sessionStorage.getItem(TWO_FACTOR_PENDING_KEY);
}

export function clearPendingTwoFactorToken() {
  sessionStorage.removeItem(TWO_FACTOR_PENDING_KEY);
  sessionStorage.removeItem(TWO_FACTOR_REDIRECT_KEY);
}

export function persistLoginToken(token: string) {
  const storageSuccess = StorageManager.setToken(token);
  if (!storageSuccess) {
    throw new Error('Failed to store authentication token');
  }
}

export async function setupTwoFactor(pendingToken?: string): Promise<TwoFactorSetupResponse> {
  const response = await api.post<TwoFactorSetupResponse>('/auth/2fa/setup', {
    pendingToken: pendingToken || getPendingTwoFactorToken(),
  }, pendingToken ? { headers: { 'X-Two-Factor-Pending': pendingToken } } : undefined);
  return response.data;
}

export async function confirmTwoFactor(code: string, pendingToken?: string): Promise<AuthResponse> {
  const token = pendingToken || getPendingTwoFactorToken();
  const response = await api.post<AuthResponse>('/auth/2fa/confirm', { code, pendingToken: token });
  if (response.data.token) {
    persistLoginToken(response.data.token);
    clearPendingTwoFactorToken();
  }
  return response.data;
}

export async function verifyTwoFactorLogin(code: string, backupCode?: string): Promise<AuthResponse> {
  const pendingToken = getPendingTwoFactorToken();
  if (!pendingToken) throw new Error('Two-factor session expired. Please sign in again.');

  const response = await api.post<AuthResponse>('/auth/2fa/verify-login', {
    code: backupCode ? undefined : code,
    backupCode,
    pendingToken,
  });

  if (response.data.token) {
    persistLoginToken(response.data.token);
    clearPendingTwoFactorToken();
  }
  return response.data;
}

export async function getTwoFactorStatus(): Promise<TwoFactorStatusResponse> {
  const response = await api.get<TwoFactorStatusResponse>('/auth/2fa/status');
  return response.data;
}

export async function disableTwoFactor(password: string, code: string): Promise<void> {
  await api.post('/auth/2fa/disable', { password, code });
}

export async function regenerateBackupCodes(password: string, code: string): Promise<string[]> {
  const response = await api.post<{ backupCodes: string[] }>('/auth/2fa/regenerate-backup-codes', {
    password,
    code,
  });
  return response.data.backupCodes;
}

export function redirectPathForLoginResponse(response: AuthResponse, isAdmin = false): string {
  if (isAdmin) return '/admin/dashboard';
  if (response.agency?.slug) return `/agencies/${response.agency.slug}/dashboard`;
  return '/dashboard';
}

export function redirectPathForTwoFactorMode(mode: 'setup' | 'verify', isAdmin = false, agencySlug?: string): string {
  if (mode === 'setup') {
    if (isAdmin) return '/admin/dashboard';
    if (agencySlug) return `/agencies/${agencySlug}/dashboard`;
    return '/dashboard';
  }
  if (isAdmin) return '/admin/dashboard';
  if (agencySlug) return `/agencies/${agencySlug}/dashboard`;
  return '/dashboard';
}
